import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { VaultErrorSection } from '../session/VaultErrorSection';
import { usePlan } from '../plan/usePlan';
import { buildCorpus } from './corpus';
import type { CorpusItem } from './corpus';
import { buildGathering } from './gathering';
import { HORIZONS, VIEWS, VIEW_LABELS, inHorizon, inView } from './view';
import type { Horizon, View } from './view';
import { useThoughtWindow } from './useThoughtWindow';
import { groupByDay, ThoughtDays, useColumnCount } from './ThoughtWall';
import { useCatalog } from '../entity/catalogStore';
import { hiddenStateOf } from '../entity/visibility';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import { cardFill } from '../design/tokens';
import { useTheme } from '../design/useTheme';
import { tagMarkOf } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';
import { Loading } from '../shell/Loading';
import { useProvideOverrideBar } from '../shell/overrideBar';
import { SelectionBar } from '../board/SelectionBar';
import { AddToFlow } from '../membership/AddDialogs';
import { usePublishSelection } from '../membership/selection';
import { captureItem } from '../membership/membership';
import type { Item } from '../membership/membership';
import './thoughts.css';

/**
 * Everything that has been said, newest first, as cards.
 *
 * The plan board is for deciding what is next; this is for seeing what has
 * been captured. One layout — cards, grouped by day, packed three across —
 * and nothing counted: which ones and how far back live behind a quiet ⋯.
 */

/** Cards rendered before the scroll asks for more. Reads follow rendering, so
 *  this is also how much decryption a first paint costs. */
const PAGE = 60;

export function ThoughtsRoute() {
  const { state, reload } = useVault();
  const navigate = useNavigate();
  // Shared, so this list and the board agree about which arc cites what.
  const { plan } = usePlan();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : null;
  const registries = plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES;
  const { theme } = useTheme();

  const [view, setView] = useState<View>('everything');
  const [horizon, setHorizon] = useState<Horizon>('2 weeks');
  // The one deliberate control that reveals what the vault marks done,
  // archived or hidden — off is the resting state, matching the app's feeds.
  const [showHidden, setShowHidden] = useState(false);
  const narrowed = view !== 'everything' || horizon !== '2 weeks' || showHidden;
  const [limit, setLimit] = useState(PAGE);
  const [focus, setFocus] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  const columns = useColumnCount(list);
  const sentinel = useRef<HTMLDivElement>(null);

  const vault = state.status === 'ready' ? state.vault : null;
  const corpus = useMemo(() => (vault ? buildCorpus(vault) : []), [vault]);

  // The catalog, joined by path, shared with the folders surface.
  const states = useCatalog();
  /** A card wears its thought's first tag; a thought in no tag, the plain
   *  card. */
  const fillOf = (path: string): string => {
    const tag = states.get(path)?.tags[0];
    return cardFill(theme, tag ? tagTint(registries, tag) : null);
  };
  const markOf = (path: string): TagMark | null =>
    tagMarkOf(states.get(path)?.tags ?? [], registries.folders, theme);

  const nowMs = useMemo(() => Date.now(), [corpus]);
  // THE visibility gate — applied once, here. hiddenStateOf is the one law
  // (entity/visibility).
  const visibleCorpus = useMemo(
    () => (showHidden ? corpus : corpus.filter((item) => !hiddenStateOf(states.get(item.path), nowMs))),
    [corpus, states, showHidden, nowMs],
  );

  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const chosen = useMemo(() => {
    const byPath = new Map(corpus.map((c) => [c.path, c]));
    return [...picked].flatMap((path): Item[] => {
      const c = byPath.get(path);
      const item = c && captureItem(c, states.get(path)?.name ?? null);
      return item ? [item] : [];
    });
  }, [picked, corpus, states]);
  usePublishSelection(chosen, () => setPicked(new Set()));
  const toggle = (item: CorpusItem) =>
    setPicked((p) => {
      const next = new Set(p);
      if (!next.delete(item.path)) next.add(item.path);
      return next;
    });
  const clearPicked = () => setPicked(new Set());

  useProvideOverrideBar(
    chosen.length > 0 ? (
      <SelectionBar count={chosen.length} noun="thought" onAddTo={() => setAdding(true)} onClear={clearPicked} />
    ) : null,
  );

  const gathering = useMemo(() => buildGathering(arcs ?? []), [arcs]);
  const now = useMemo(() => new Date(), [corpus]);

  const selected = useMemo(
    () => visibleCorpus.filter((item) => inView(item, view, gathering) && inHorizon(item, horizon, now)),
    [visibleCorpus, view, gathering, horizon, now],
  );

  const rendered = selected.slice(0, limit);
  const opened = useThoughtWindow(
    vault,
    useMemo(() => rendered.map((i) => i.path), [rendered]),
  );

  const shown = useMemo(
    () => rendered.filter((item) => !opened.get(item.path)?.tombstone),
    [rendered, opened],
  );

  const days = useMemo(() => groupByDay(shown, now), [shown, now]);

  useEffect(() => {
    setLimit(PAGE);
    setFocus(0);
  }, [view, horizon, showHidden]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || limit >= selected.length) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setLimit((n) => n + PAGE);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [limit, selected.length]);

  // j / k walk the cards newest first, ↵ opens one. Never while typing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing || event.metaKey || event.ctrlKey) return;
      if (event.key === 'Escape') setPicked((p) => (p.size > 0 ? new Set() : p));
      else if (event.key === 'j') setFocus((f) => Math.min(f + 1, shown.length - 1));
      else if (event.key === 'k') setFocus((f) => Math.max(f - 1, 0));
      else if (event.key === 'Enter') {
        const item = shown[focus];
        if (item) navigate(fileHref(item));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown, focus, navigate]);

  useEffect(() => {
    list.current?.querySelector<HTMLElement>('[data-focused]')?.scrollIntoView({ block: 'nearest' });
  }, [focus]);

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder th-gate">
        <h1>This browser cannot read the vault yet.</h1>
        <p className="t-bodyProse">
          Everything is encrypted with a key only the phone holds. Pair once and the list fills in as
          you scroll.
        </p>
        <Link className="th-pair" to="/connect">
          Pair this browser
        </Link>
      </section>
    );
  }

  if (state.status === 'error') return <VaultErrorSection state={state} reload={reload} />;

  if (state.status === 'loading') return <Loading title="Opening the vault…" />;

  if (corpus.length === 0) {
    return (
      <section className="empty">
        <h1>Nothing captured yet.</h1>
        <p className="t-bodyProse">Say something on the phone and it will be here.</p>
      </section>
    );
  }

  return (
    <div className="thw" ref={list}>
      <div className="thw-top">
        <ThoughtsMenu
          view={view}
          horizon={horizon}
          showHidden={showHidden}
          narrowed={narrowed}
          onView={setView}
          onHorizon={setHorizon}
          onShowHidden={setShowHidden}
        />
      </div>

      <ThoughtDays
        days={days}
        columns={columns}
        opened={opened}
        gathering={gathering}
        fillOf={fillOf}
        markOf={markOf}
        focused={shown[focus]?.path ?? null}
        selected={picked}
        onSelect={toggle}
        onOpen={(item) => navigate(fileHref(item))}
      />

      {shown.length === 0 && (
        <section className="empty">
          <h1>Nothing here.</h1>
          <p className="t-bodyProse">
            {narrowed ? 'Nothing matches what ⋯ is showing.' : 'Nothing was captured in the last two weeks.'}
          </p>
        </section>
      )}

      <div ref={sentinel} className="th-sentinel" />

      {adding && <AddToFlow items={chosen} onDone={clearPicked} onClose={() => setAdding(false)} />}
    </div>
  );
}

/**
 * Which thoughts are on the wall and how far back, behind one quiet ⋯ — the
 * place Plan keeps its switch. No counts. The ⋯ carries a dot while it holds
 * anything back, because a narrowing nobody can see is how a page quietly
 * hides things.
 */
function ThoughtsMenu({
  view,
  horizon,
  showHidden,
  narrowed,
  onView,
  onHorizon,
  onShowHidden,
}: {
  view: View;
  horizon: Horizon;
  showHidden: boolean;
  narrowed: boolean;
  onView: (view: View) => void;
  onHorizon: (horizon: Horizon) => void;
  onShowHidden: (show: boolean) => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.thw-more')) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <span className="thw-more">
      <button
        type="button"
        className={`head-icon${narrowed ? ' is-narrowed' : ''}`}
        aria-label="Which thoughts"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
      {open && (
        <div className="head-menu thw-menu" role="menu">
          <span className="thw-menu-label">How far back</span>
          {HORIZONS.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={horizon === option}
              onClick={() => onHorizon(option)}
            >
              {option}
            </button>
          ))}
          <span className="thw-menu-label">Which</span>
          {VIEWS.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={view === option}
              onClick={() => onView(option)}
            >
              {VIEW_LABELS[option]}
            </button>
          ))}
          <span className="thw-menu-sep" />
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={showHidden}
            onClick={() => onShowHidden(!showHidden)}
          >
            Show done, archived and snoozed
          </button>
        </div>
      )}
    </span>
  );
}

function fileHref(item: CorpusItem): string {
  return `/file/${item.path.split('/').map(encodeURIComponent).join('/')}`;
}
